import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook pour gérer la connexion WebSocket à une session
 * 
 * @param {number} sessionId - ID de la session à connecter
 * @param {string} accessToken - Token JWT pour l'authentification
 * @param {function} onMessage - Callback appelé à chaque message reçu
 * @returns {object} { isConnected, send, connectionCount, reconnect }
 */
export function useWebSocket(sessionId, accessToken, onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionCount, setConnectionCount] = useState(1); // Nombre de connexions (au moins 1 = soi-même)
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const isMountedRef = useRef(true); // ✅ Track si le composant est monté
  const onMessageRef = useRef(onMessage); // ✅ Stabiliser le callback
  
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000; // 2 secondes
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const WS_URL = API_BASE_URL.replace(/^http/, 'ws'); // http -> ws, https -> wss
  const isConnectingRef = useRef(false); // ✅ Flag pour éviter connexions multiples simultanées

  // ✅ Mettre à jour la ref quand onMessage change
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  /**
   * Envoie un message via WebSocket
   */
  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isAuthenticatedRef.current) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket non connecté ou non authentifié, impossible d\'envoyer:', message);
      return false;
    }
  }, []);

  /**
   * Connecte le WebSocket
   */
  const connect = useCallback(() => {
    if (!sessionId || !accessToken) {
      console.warn('sessionId ou accessToken manquant, impossible de connecter WebSocket');
      return;
    }

    // ✅ Empêcher connexions multiples simultanées
    if (isConnectingRef.current) {
      return;
    }

    // Nettoyer la connexion existante si elle existe
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    isConnectingRef.current = true;
    const ws = new WebSocket(`${WS_URL}/ws/${sessionId}`);
    wsRef.current = ws;
    isAuthenticatedRef.current = false;

    ws.onopen = () => {
      isConnectingRef.current = false; // ✅ Connexion établie
      // Envoyer le message d'authentification
      ws.send(JSON.stringify({
        type: 'auth',
        token: accessToken
      }));
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        // Gérer les messages système
        if (message.type === 'auth_success') {
          isAuthenticatedRef.current = true;
          setIsConnected(true);
          reconnectAttemptsRef.current = 0; // Reset compteur de reconnexion
          // ✅ Récupérer le nombre de connexions actives
          if (message.connection_count !== undefined) {
            setConnectionCount(message.connection_count);
          }
        } else if (message.type === 'auth_error') {
          console.error('❌ Erreur authentification WebSocket:', message.message);
          ws.close();
          return;
        } else if (message.type === 'user_joined' || message.type === 'user_left') {
          if (message.connection_count !== undefined) {
            setConnectionCount(message.connection_count);
          }
        }

        // Appeler le callback pour tous les messages
        if (onMessageRef.current) {
          onMessageRef.current(message);
        }
      } catch (error) {
        console.error('Erreur parsing message WebSocket:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ Erreur WebSocket:', error);
      isConnectingRef.current = false; // ✅ Reset le flag en cas d'erreur
    };

    ws.onclose = (event) => {
      isAuthenticatedRef.current = false;
      isConnectingRef.current = false; // ✅ Reset le flag
      setIsConnected(false);

      // ✅ Ne pas reconnecter si le composant est démonté
      if (!isMountedRef.current) {
        return;
      }

      // Tentative de reconnexion automatique (sauf si fermeture normale)
      if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) { // ✅ Double check avant reconnexion
            connect();
          }
        }, RECONNECT_DELAY);
      } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error('❌ Nombre maximum de tentatives de reconnexion atteint');
      }
    };
  }, [sessionId, accessToken, WS_URL, onMessage]);

  /**
   * Force une reconnexion manuelle
   */
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Connexion initiale
  useEffect(() => {
    isMountedRef.current = true; // ✅ Marquer comme monté
    connect();

    // Cleanup à la déconnexion
    return () => {
      isMountedRef.current = false; // ✅ Marquer comme démonté
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [sessionId, accessToken, WS_URL]); // ✅ Dépendances stables au lieu de connect

  return {
    isConnected,
    send,
    connectionCount,
    reconnect
  };
}
